import React, { createContext, useCallback, useState, useEffect } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '../config/apiBase';
const API_URL = `${API_BASE_URL}/users`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(API_URL, { headers: getAuthHeaders() });
      setUsers(response.data);
      setUsersError(null);
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      setUsers([]);
      setUsersError(status === 403 ? 'forbidden' : 'error');
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      if (!localStorage.getItem('token')) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await fetchUsers();
      } catch (error) {
        if (error?.response?.status !== 403) {
          console.error('Erro ao buscar usuários:', error);
        }
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [fetchUsers]);

  const addUser = async (user) => {
    try {
      const response = await axios.post(API_URL, user, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      setUsers((prev) => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error('Erro ao adicionar usuário:', error);
      throw error;
    }
  };

  const editUser = async (id, user) => {
    try {
      const response = await axios.put(`${API_URL}/${id}`, user, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      setUsers((prev) => prev.map((u) => (u.id === id ? response.data : u)));
      return response.data;
    } catch (error) {
      console.error('Erro ao editar usuário:', error);
      throw error;
    }
  };

  const deleteUser = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: getAuthHeaders(),
      });
      setUsers((prev) => prev.filter((u) => u.id !== id));
      return { success: true };
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      throw error;
    }
  };

  return (
    <UserContext.Provider value={{ users, loading, usersError, fetchUsers, addUser, editUser, deleteUser }}>
      {children}
    </UserContext.Provider>
  );
};